// Revisado: 2026-02-21 [I18n Scan] - [Meta SEO]
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import FadeIn from './components/shared/FadeIn';
import SEO from './components/shared/SEO';
import { ROUTES } from './routes';

const About = () => {
    const { t } = useTranslation();

    return (
        <FadeIn>
            <div className="min-h-screen bg-gray-900 text-white p-6 md:p-12 relative overflow-hidden">
                <SEO title={t('MetaAbout.title')} description={t('MetaAbout.description')} />

                {/* Background Decor */}
                <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-10">
                    <div className="absolute top-20 right-20 w-96 h-96 bg-primary-green rounded-full blur-[150px]"></div>
                    <div className="absolute bottom-20 left-20 w-80 h-80 bg-secondary-blue rounded-full blur-[150px]"></div>
                </div>

                <div className="max-w-5xl mx-auto relative z-10">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <h1 className="text-4xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-500 mb-6 drop-shadow-md">
                            {t('About.title', 'Sobre UmaCalculator')}
                        </h1>
                        <div className="h-1 w-24 bg-gradient-to-r from-emerald-500 to-cyan-500 mx-auto rounded-full"></div>
                    </div>

                    {/* Content Logic */}
                    <div className="space-y-12">

                        {/* Section 1: Intro */}
                        <div className="bg-gray-800/50 backdrop-blur-md border border-gray-700 rounded-2xl p-8 hover:border-emerald-500/30 transition-colors">
                            <h2 className="text-2xl font-bold text-emerald-400 mb-4 flex items-center gap-3">
                                <span className="text-3xl">🚀</span>
                                {t('About.what_is_title', '¿Qué es?')}
                            </h2>
                            <p className="text-gray-300 leading-relaxed text-lg">
                                {t('About.what_is_desc', 'UmaCalculator es una herramienta web nacida tras el lanzamiento de Umamusume Pretty Derby en 2025, creada con el objetivo principal de calcular los scores de los personajes.')}
                            </p>
                        </div>

                        {/* Section 2: Origin Story */}
                        <div className="bg-gray-800/50 backdrop-blur-md border border-gray-700 rounded-2xl p-8 hover:border-blue-500/30 transition-colors">
                            <h2 className="text-2xl font-bold text-blue-400 mb-4 flex items-center gap-3">
                                <span className="text-3xl">🌱</span>
                                {t('About.origin_title', 'Nuestro Origen')}
                            </h2>
                            <div className="space-y-4 text-gray-300 leading-relaxed">
                                <p>
                                    {t('About.origin_text_1', 'La historia comienza con un amigo y una simple hoja de cálculo. Su objetivo original era modesto pero útil: visualizar el calendario de carreras y saber qué personaje correría cada día. Poco a poco, él integró un seguimiento básico de datos, hasta que decidí tomar ese archivo para expandirlo.')}
                                </p>
                                <p>
                                    {t('About.origin_text_2', 'Fui agregando funcionalidades lentamente a ese Excel, incorporando desde cálculos complejos hasta predicciones de eventos. En ese proceso, el archivo terminó perdiendo el "norte" de la necesidad original de calendario que buscaba mi amigo, transformándose en una hoja de cálculo sumamente versátil, pero muy distinta a su propósito inicial.')}
                                </p>
                            </div>
                        </div>

                        {/* Section 3: Current Focus & Calculation */}
                        <div className="bg-gray-800/50 backdrop-blur-md border border-gray-700 rounded-2xl p-8 hover:border-purple-500/30 transition-colors">
                            <h2 className="text-2xl font-bold text-purple-400 mb-4 flex items-center gap-3">
                                <span className="text-3xl">🎯</span>
                                {t('About.focus_title', 'El Enfoque Actual')}
                            </h2>
                            <div className="space-y-6 text-gray-300 leading-relaxed">
                                <p>
                                    {t('About.focus_text', 'Actualmente, el juego ha implementado sus propias formas de visualizar las carreras, lo que hizo que el Excel original perdiera su sentido. Lentamente, aquella herramienta que creamos comenzó a tener menos dirección y se llenó de funciones que, si bien eran más objetivas, no aportaban mucho valor real frente a las actualizaciones del juego.')}
                                </p>
                                <p className="font-semibold text-white">
                                    {t('About.focus_conclusion', 'Debido a esto, UmaCalculator se convirtió completamente en un calculador del score final del personaje.')}
                                </p>

                                <div className="mt-6 pt-6 border-t border-gray-700">
                                    <h3 className="text-xl font-bold text-white mb-3">
                                        {t('About.how_title', '¿Cómo calculamos el score?')}
                                    </h3>
                                    <p>
                                        {t('About.how_text', 'Contamos con una base de datos que almacena toda la información necesaria para computar los puntos. Esta nos permite conocer los stats de los personajes, el puntaje que entregan las skills y la influencia que tienen las aptitudes. Todo esto trabaja en conjunto con métodos de cálculo automático para entregar el score final de manera precisa.')}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Section 4: Disclaimer (Footer Style) */}
                        <div className="opacity-70 text-sm p-4 border-t border-gray-800 text-center text-gray-400">
                            <p className="mb-2 font-bold text-yellow-500/80 uppercase tracking-widest text-xs">
                                {t('About.disclaimer_label', 'AVISO LEGAL')}
                            </p>
                            <p className="italic max-w-2xl mx-auto">
                                {t('About.disclaimer_mini', 'Este es un proyecto hecho por fans y para fans. No estamos afiliados con Cygames, Inc. Todos los derechos de imágenes y personajes pertenecen a sus respectivos dueños.')}
                            </p>
                        </div>

                        {/* Navigation */}
                        <div className="flex justify-center pt-8">
                            <Link
                                to={ROUTES.MAIN}
                                className="px-8 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-full transition-all shadow-lg hover:shadow-emerald-500/20 font-bold flex items-center gap-2"
                            >
                                <span>🏠</span>
                                {t('About.btn_home', 'Volver al Inicio')}
                            </Link>
                        </div>

                    </div>

                    {/* Footer Logo/Decor */}
                    <div className="mt-20 text-center opacity-30">
                        <div className="text-6xl mb-4">🐎</div>
                        <p className="text-sm font-mono tracking-widest text-emerald-500">UMAMUSUME CALCULATOR</p>
                    </div>
                </div>
            </div>
        </FadeIn>
    );
};

export default About;
